<template>
  <div v-if="isViewReady" class="velg">
    <div class="mt-3 text-center">
      <div class="small-container">
        <div class="mb-5">
          <hl-H1 class="text-left">Hva ønsker du å gjøre nå, {{ signup.user.fullName }}?</hl-H1>
          <p class="text-left">Du har nå laget deg en personlig bruker, opprettet utleierprofil og registrert et
            leieobjekt. Nå må du velge et av alternativene under for å gå videre.</p>
        </div>

        <div v-if="getTargetAction(targetAction)" class="mb-4">
          <a
            :href="getLink(getTargetAction(targetAction).name)"
            @click="handleTargetAction(getTargetAction(targetAction).name)"
            rel="noopener"
            class="btn btn-primary btn-block p-4 text-white"
          >
            <i :class="getTargetAction(targetAction).iconClass" class="d-block mb-3" style="font-size: 2.3rem"></i>
            <p style="font-size: 1.2rem" class="mb-0">
              {{ getTargetAction(targetAction).title }}
            </p>
          </a>
        </div>

        <h5 v-if="getTargetAction(targetAction)" class="text-left mt-5 mb-4">Andre valg</h5>

        <div class="row mb-5 equal-height">
          <div v-for="(action, i) in getOtherActions(targetAction)" :key="i" class="col-12 col-md-4 mb-3">
            <a style="color: inherit; text-decoration: none">
              <hl-ButtonTapWithIcon
                :iconClass="action.iconClass"
                layout="vertical"
                @click.native="handleTargetAction(action.name)"
                :link="getLink(action.name)"
                :target="getTargetAction(targetAction) ? '_blank' : '_self'"
              >
                <p v-html="action.title"></p>
              </hl-ButtonTapWithIcon>
            </a>
          </div>
        </div>
      </div>
    </div>

    <hl-Modal
      v-if="showPOSTErrorModal"
      iconClass="budicon-cloud-server text-warning"
      title="Noe gikk dessverre galt! Forsøk igjen om noen få minutter. Vi er på saken."
    >
      <hl-Loader v-if="loading" variation="warning" />

      <div v-else>
        <button class="btn btn-warning mx-2" @click="signupUser">
          Prøv igjen
        </button>
        <button
          class="btn btn-outline-secondary mx-2"
          @click="$router.push({ name: 'home' })"
        >
          Avbryt
        </button>
      </div>
    </hl-Modal>
  </div>
</template>

<script>
import husleieapi from '@modules/husleie-api';
import RedirectOperations from '@modules/husleie-framework/src/static-classes/RedirectOperations.class';
import {AppRedirect} from '@modules/husleie-framework/src/enums/AppRedirect.enum';
import {LandlordTypeEnum} from '@modules/husleie-framework/src/types/Landlord.entity';
import H1 from '@modules/husleie-framework/src/components/headings/H1.vue';
import ButtonTapWithIcon from '@modules/husleie-framework/src/components/buttons/ButtonTapWithIcon.vue';
import Modal from '@modules/husleie-framework/src/components/modals/Modal';
import Loader from '@modules/husleie-framework/src/components/loaders/Loader.vue';
import LogOperations from '@modules/husleie-framework/src/static-classes/LogOperations.class.ts';
import { getPostHog } from '@modules/husleie-framework/src/posthog.ts';

const hlApi = new husleieapi();

export default {
  name: 'Velg',

  components: {
    'hl-H1': H1,
    'hl-ButtonTapWithIcon': ButtonTapWithIcon,
    'hl-Modal': Modal,
    'hl-Loader': Loader,
  },

  props: {
    signup: {
      type: Object,
      required: true,
    },
    targetAction: String,
    redirect: String,
  },

  data() {
    return {
      isViewReady: false,
      successfulSignup: false,
      showPOSTErrorModal: false,
      apiData: {},
      actions: [
        {
          name: 'ad',
          title: 'Opprett annonse',
          iconClass: 'budicon-house-a',
        },
        {
          name: 'contract',
          title: 'Opprett leiekontrakt',
          iconClass: 'budicon-document-page',
        },
        {
          name: 'rentadm',
          title: 'Registrer eksisterende leieforhold',
          iconClass: 'budicon-deal'
        },
        {
          name: 'depAccount',
          title: 'Opprett depositumskonto',
          iconClass: 'budicon-bank',
        },
        {
          name: 'termination',
          title: 'Avslutt leieforhold',
          iconClass: 'budicon-open-door',
        },
        {
          name: 'dashboard',
          title: 'Ingenting nå. Gå til MinHusleie',
          iconClass: 'budicon-like',
        }
      ],
    };
  },

  methods: {
    getTargetAction(name) {
      return this.actions.find(item => item.name === name);
    },

    getOtherActions(name) {
      return this.actions.filter(item => item.name !== name);
    },

    getLink(dest) {
      let token = this.apiData.token;

      // TODO What to do if token is not set? This might happen on refresh of this page. Should we store the result of the createUser in localStorage or something similar?

      let redirect_url = (process.env.NODE_ENV === 'development') ? 'http://' + location.hostname + ':' + '8020' : '';

      switch (dest) {
        case 'contract':
          redirect_url = RedirectOperations.getURL(AppRedirect.CONTRACT_CREATE, {
            rentalObjectId: this.apiData.rental_object_id,
            fromSignup: true
          });
          break;
        case 'depAccount':
          switch (this.signup.landlord.type) {
            case 'private':
              redirect_url = RedirectOperations.getURL(AppRedirect.DEPOSIT_ACCOUNT, {
                landlordId: this.apiData.landlord_id,
                landlordName: this.signup.user.fullName,
                landlordType: LandlordTypeEnum.PRIVATE,
                rentalObjectId: this.apiData.rental_object_id,
                fromSignup: true
              });
              break;
            case 'company':
              redirect_url = RedirectOperations.getURL(AppRedirect.DEPOSIT_ACCOUNT, {
                landlordId: this.apiData.landlord_id,
                landlordType: LandlordTypeEnum.PROFESSIONAL,
                rentalObjectId: this.apiData.rental_object_id,
                companyName: this.signup.landlord.company.name,
                companyOrgNumber: this.signup.landlord.company.orgNumber,
                fromSignup: true
              });
              break;
          }
          break;
        case 'ad':
          redirect_url = RedirectOperations.getURL(AppRedirect.AD_CREATE, {
            landlordId: this.apiData.landlord_id,
            rentalObjectId: this.apiData.rental_object_id,
          });
          break;
        case 'rentadm':
          redirect_url += '/dashboard/landlord/' + this.apiData.landlord_id + '/rentalObject/' + this.apiData.rental_object_id + '/tenancies/create';
          break;
        case 'termination':
          redirect_url = RedirectOperations.getURL(AppRedirect.TENANCY_TERMINATION, {rentalObjectId: this.apiData.rental_object_id});
          break;
        default:
          redirect_url = RedirectOperations.getURL(AppRedirect.DASHBOARD_MYPAGE);
      }

      return RedirectOperations.getURL(AppRedirect.LOGIN, {redirectURL: redirect_url, token});
    },

    async signupUser() {
      if (this.successfulSignup) {
        return;
      }

      this.loading = true;

      let opts = {body: this.signup}; // body

      // Workaround for strict api rules...
      if (opts.body.landlord.type === 'company') {
        delete opts.body.landlord['dateOfBirth'];
      }

      try {
        const resp = await hlApi.callSynchronized('SignupApi.signup', opts);

        this.apiData = resp;
        this.successfulSignup = true;
      } catch (error) {
        LogOperations.error('API|signup', error);
        this.showPOSTErrorModal = true;
      } finally {
        this.loading = false;
      }
    },

    initPostHog() {
      const postHog = getPostHog();

      postHog?.identify(`${this.apiData.user_id}`, {
        hasSubscription: false,
      });
    },

    sendPostHogSignedUpEvent() {
      const postHog = getPostHog();

      const { user, landlord } = this.signup;
      const nameParts = user.fullName.split(' ');

      postHog?.capture('landlord_signed_up', {
        landlord_type: landlord.type,
        first_name: nameParts[0],
        last_name: nameParts.slice(1).join(' '),
        phone: user.cellPhone,
        email: user.email,
      });
    },

    handleTargetAction(action) {
      if (this.successfulSignup) {
        const postHog = getPostHog();

        postHog?.capture('landlord_signup_action', {
          action,
        });

        window.dataLayer = window.dataLayer || [];
        window.dataLayer.push({
          'event': 'VirtualPageview',
          'virtualPageURL': '/signup/step5/completed',
          'virtualPageTitle': 'Signup | Step 5 | Completed',
          'chosenActivity': action,
        });
      }
    },
  },

  async created() {
    await this.signupUser();

    this.initPostHog();
    this.sendPostHogSignedUpEvent();

    /* Tracking GTM */
    window.datalayer = window.datalayer || [];
    window.dataLayer.push({
      'event': 'VirtualPageview',
      'virtualPageURL': '/signup/step4/chooseActivity',
      'virtualPageTitle': 'Signup | Step 4 | Choose Activity',
      'customeType': this.signup.landlord.type,
      'rentalObject': this.signup.rentalObject.category,
    });

    const safeRedirect = RedirectOperations.validateRedirectUrl(this.redirect);
    if (safeRedirect) {
      window.location.href = RedirectOperations.getURL(AppRedirect.LOGIN, { redirectURL: safeRedirect, token: this.apiData.token });
    }

    this.isViewReady = true;
  },
};
</script>
